import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PortalLayout from '@/components/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { api, formatApiError } from '@/App';
import useFormDraft from '@/hooks/useFormDraft';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
  Lightning, Drop, Plugs, CurrencyCircleDollar, HouseLine,
  ChartLineUp, FileText, Scales, ArrowCounterClockwise, Leaf,
  CheckCircle, Upload, Trash, Warning, CloudArrowUp, UserPlus, FloppyDisk
} from '@phosphor-icons/react';

const SERVICE_TYPES = [
  { id: 'Water Query', icon: Drop },
  { id: 'Electricity Query', icon: Lightning },
  { id: 'New Connection', icon: Plugs },
  { id: 'New Account Opening', icon: UserPlus },
  { id: 'Payment Arrangement', icon: CurrencyCircleDollar },
  { id: 'Building Plans', icon: HouseLine },
  { id: 'Tariff Change Application', icon: ChartLineUp },
  { id: 'Billing Query', icon: FileText },
  { id: 'Value Dispute', icon: Scales },
  { id: 'Refund Request', icon: ArrowCounterClockwise },
  { id: 'Energy Audit', icon: Leaf },
];

const DOCUMENT_REQUIREMENTS = {
  'Building Plans': ['Professionally drawn building plans', 'Proof of ownership'],
  'Water Query': ['Municipality Bill', 'Identification document'],
  'Electricity Query': ['Municipality bill', 'ID copy'],
  'New Connection': ['Company documents', 'ID copy'],
  'New Account Opening': ['ID copy', 'Proof of address', 'Proof of ownership / tenancy'],
  'Payment Arrangement': ['Municipality bill', 'ID copy'],
  'Billing Query': ['Municipal bill', 'ID copy'],
  'Value Dispute': ['Municipality bill', 'ID copy', 'Supporting evidence'],
  'Refund Request': ['Municipality bill', 'ID copy', 'Proof of payment'],
  'Tariff Change Application': ['Municipality bill', 'ID copy'],
};

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
const MAX_SIZE = 10 * 1024 * 1024; // 10MB

const PortalServiceRequest = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData, { clearDraft, hasDraft, savedAt }] = useFormDraft(
    'portal_service_request',
    { company_id: '', service_type: '', public_notes: '', priority: 'Medium' }
  );
  const [files, setFiles] = useState({}); // { "Municipality Bill": File, ... }  — NOT auto-saved
  const [skipDocs, setSkipDocs] = useState(false);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const { data } = await api.get('/dashboard/client');
        const list = data.companies || [];
        setCompanies(list);
        const preselect = location.state?.preselectCompanyId;
        if (preselect && list.find(c => c.company_id === preselect)) {
          setFormData(prev => ({ ...prev, company_id: preselect }));
        } else if (list.length === 1 && !formData.company_id) {
          setFormData(prev => ({ ...prev, company_id: list[0].company_id }));
        }
        // If draft has a company_id that no longer exists, clear it
        if (!preselect && formData.company_id && !list.find(c => c.company_id === formData.company_id)) {
          setFormData(prev => ({ ...prev, company_id: '' }));
        }
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requiredDocs = DOCUMENT_REQUIREMENTS[formData.service_type] || [];

  const handleFileChange = (docType, file) => {
    if (!file) { setFiles(prev => { const n = { ...prev }; delete n[docType]; return n; }); return; }
    if (!ALLOWED_TYPES.includes(file.type)) { toast.error(`${file.name}: Only PDF, JPG, PNG files allowed`); return; }
    if (file.size > MAX_SIZE) { toast.error(`${file.name}: File exceeds 10MB limit`); return; }
    setFiles(prev => ({ ...prev, [docType]: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_id) { toast.error('Please select a company'); return; }
    if (!formData.service_type) { toast.error('Please select a service type'); return; }

    // Check required docs unless user opted out
    if (!skipDocs) {
      const missing = requiredDocs.filter(d => !files[d]);
      if (missing.length > 0) { toast.error(`Please upload: ${missing.join(', ')}`); return; }
    }

    setSubmitting(true);
    try {
      // Create service request
      const { data: caseData } = await api.post('/service-requests', formData);

      // Upload documents only if user provided them
      if (!skipDocs && Object.keys(files).length > 0) {
        const fd = new FormData();
        const docTypes = [];
        for (const [docType, file] of Object.entries(files)) {
          fd.append('files', file);
          docTypes.push(docType);
        }
        await api.post(`/service-requests/${caseData.case_id}/documents?document_types=${encodeURIComponent(docTypes.join(','))}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      toast.success(skipDocs ? 'Service request submitted! Marema will contact you about required documents.' : 'Service request submitted with documents!');
      clearDraft();
      navigate(`/portal/cases/${caseData.case_id}`);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally { setSubmitting(false); }
  };

  if (loading) return <PortalLayout title="New Service Request"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-[#4CAF50] border-t-transparent rounded-full animate-spin"></div></div></PortalLayout>;

  if (companies.length === 0) {
    return (
      <PortalLayout title="New Service Request">
        <div className="max-w-2xl mx-auto"><Card><CardContent className="p-8 text-center">
          <Warning className="w-12 h-12 text-orange-400 mx-auto mb-4" /><h3 className="text-lg font-semibold mb-2">No Companies Linked</h3><p className="text-gray-600">Your account is not linked to any companies. Please contact support at info@maremaconsulting.co.za</p>
        </CardContent></Card></div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout title="New Service Request">
      <div className="max-w-3xl mx-auto space-y-6 fade-in">
        <div>
          <h1 className="text-2xl font-bold text-[#0A192F]">New Service Request</h1>
          <p className="text-sm text-gray-500 mt-1">Submit a new request and upload required documents</p>
        </div>

        {hasDraft && (formData.service_type || formData.public_notes || formData.company_id) && (
          <div className="flex items-start justify-between gap-3 p-3 border border-blue-200 bg-blue-50" data-testid="draft-banner">
            <div className="flex items-start gap-2">
              <FloppyDisk className="w-4 h-4 mt-0.5 text-blue-600" weight="fill" />
              <div>
                <p className="text-sm text-blue-900 font-medium">Draft restored</p>
                <p className="text-xs text-blue-800">Your form is auto-saved as you type. {savedAt && (<span>Last saved {new Date(savedAt).toLocaleTimeString()}</span>)}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => { clearDraft(); setFormData({ company_id: '', service_type: '', public_notes: '', priority: 'Medium' }); setFiles({}); setSkipDocs(false); }}
              className="text-xs text-blue-700 underline hover:text-blue-900"
              data-testid="discard-draft-btn"
            >
              Discard draft
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Company & Service Type */}
          <Card>
            <CardHeader className="border-b pb-4"><CardTitle className="text-base">Request Details</CardTitle></CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label>Account / Company *</Label>
                <Select value={formData.company_id} onValueChange={(v) => setFormData({ ...formData, company_id: v })}>
                  <SelectTrigger className="rounded-none" data-testid="company-select"><SelectValue placeholder="Select your account..." /></SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const parents = companies.filter(c => !c.parent_company_id);
                      const children = companies.filter(c => c.parent_company_id);
                      const childrenByParent = children.reduce((acc, c) => {
                        (acc[c.parent_company_id] = acc[c.parent_company_id] || []).push(c);
                        return acc;
                      }, {});
                      const orphans = children.filter(c => !companies.find(p => p.company_id === c.parent_company_id));
                      const items = [];
                      parents.forEach(p => {
                        items.push(
                          <SelectItem key={p.company_id} value={p.company_id}>{p.company_name}</SelectItem>
                        );
                        (childrenByParent[p.company_id] || []).forEach(ch => {
                          items.push(
                            <SelectItem key={ch.company_id} value={ch.company_id}>
                              <span className="text-xs text-gray-400 mr-2">↳</span>{ch.company_name}
                              <span className="text-xs text-gray-400 ml-2">(sub-account)</span>
                            </SelectItem>
                          );
                        });
                      });
                      orphans.forEach(o => {
                        items.push(<SelectItem key={o.company_id} value={o.company_id}>{o.company_name}</SelectItem>);
                      });
                      return items;
                    })()}
                  </SelectContent>
                </Select>
                {formData.company_id && (() => {
                  const c = companies.find(x => x.company_id === formData.company_id);
                  if (c?.parent_company_id) {
                    const parent = companies.find(p => p.company_id === c.parent_company_id);
                    return <p className="text-xs text-gray-500 mt-1" data-testid="selected-child-hint">Submitting on behalf of sub-account under <strong>{parent?.company_name || 'parent'}</strong>.</p>;
                  }
                  return null;
                })()}
              </div>

              <div>
                <Label>Service Type *</Label>
                <Select value={formData.service_type} onValueChange={(v) => { setFormData({ ...formData, service_type: v }); setFiles({}); }}>
                  <SelectTrigger className="rounded-none" data-testid="service-type-select"><SelectValue placeholder="Select service type..." /></SelectTrigger>
                  <SelectContent>{SERVICE_TYPES.map(s => <SelectItem key={s.id} value={s.id}>{s.id}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div>
                <Label>Additional Notes</Label>
                <Textarea value={formData.public_notes} onChange={(e) => setFormData({ ...formData, public_notes: e.target.value })} placeholder="Describe your request..." rows={3} className="rounded-none" data-testid="notes-input" />
              </div>
            </CardContent>
          </Card>

          {/* Document Uploads - shown when service type selected */}
          {formData.service_type && requiredDocs.length > 0 && (
            <Card>
              <CardHeader className="border-b pb-4">
                <CardTitle className="text-base flex items-center gap-2">
                  <CloudArrowUp className="w-5 h-5 text-[#4CAF50]" /> Required Documents
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6" data-testid="document-uploads">
                <label className="flex items-start gap-3 p-3 mb-4 bg-amber-50 border border-amber-200 cursor-pointer" data-testid="skip-docs-toggle">
                  <input
                    type="checkbox"
                    checked={skipDocs}
                    onChange={(e) => { setSkipDocs(e.target.checked); if (e.target.checked) setFiles({}); }}
                    className="mt-1 w-4 h-4 accent-[#4CAF50]"
                    data-testid="skip-docs-checkbox"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#0A192F]">I don&apos;t have these documents right now</p>
                <p className="text-xs text-gray-600 mt-0.5">Submit the request anyway — Marema will reach out to help you collect them.</p>
                  </div>
                </label>

                {!skipDocs && (
                  <>
                    <p className="text-sm text-gray-600 mb-4">
                      The following documents are required for <strong>{formData.service_type}</strong>. Accepted formats: PDF, JPG, PNG (max 10MB each).
                    </p>
                    <div className="space-y-4">
                      {requiredDocs.map((docType) => (
                        <div key={docType} className="border border-gray-200 p-4" data-testid={`doc-upload-${docType.replace(/ /g, '-').toLowerCase()}`}>
                          <div className="flex items-center justify-between mb-2">
                            <Label className="flex items-center gap-2">
                              {files[docType] ? <CheckCircle className="w-4 h-4 text-[#4CAF50]" weight="fill" /> : <Upload className="w-4 h-4 text-gray-400" />}
                              {docType} *
                            </Label>
                            {files[docType] && (
                              <button type="button" onClick={() => handleFileChange(docType, null)} className="text-red-500 hover:text-red-700">
                                <Trash className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          {files[docType] ? (
                            <p className="text-sm text-[#4CAF50] flex items-center gap-2">
                              <FileText className="w-4 h-4" /> {files[docType].name} ({(files[docType].size / 1024).toFixed(0)} KB)
                            </p>
                          ) : (
                            <Input
                              type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileChange(docType, e.target.files?.[0])}
                          className="rounded-none text-sm"
                        />
                      )}
                    </div>
                  ))}
                </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          <Button type="submit" disabled={submitting} className="w-full bg-[#4CAF50] hover:bg-[#45a049] text-white rounded-none py-3 font-medium" data-testid="submit-request-btn">
            {submitting ? 'Submitting...' : 'Submit Service Request'}
          </Button>
        </form>
      </div>
    </PortalLayout>
  );
};

export default PortalServiceRequest;
